import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Divider,
  Typography,
} from '@mui/material';

// UI-specific export configuration
export interface UIExportConfig {
  format: 'pdf' | 'excel' | 'csv';
  includeSummary: boolean;
  includeFindings: boolean;
  includeSlideImages: boolean;
  severityFilter: string[];
}

interface ExportOptionsProps {
  config: UIExportConfig;
  onChange: (config: UIExportConfig) => void;
}

export const ExportOptions: React.FC<ExportOptionsProps> = ({ config, onChange }) => {
  const handleFormatChange = (format: 'pdf' | 'excel' | 'csv') => {
    onChange({ ...config, format });
  };

  const handleIncludeChange = (field: keyof UIExportConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...config, [field]: event.target.checked });
  };

  const handleSeverityChange = (severity: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newFilters = event.target.checked
      ? [...config.severityFilter, severity]
      : config.severityFilter.filter(s => s !== severity);
    onChange({ ...config, severityFilter: newFilters });
  };

  return (
    <Box>
      <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
        <FormLabel component="legend">Export Format</FormLabel>
        <RadioGroup
          value={config.format}
          onChange={(e) => handleFormatChange(e.target.value as 'pdf' | 'excel' | 'csv')}
        >
          <FormControlLabel value="pdf" control={<Radio />} label="PDF Report" />
          <FormControlLabel value="excel" control={<Radio />} label="Excel Spreadsheet" />
          <FormControlLabel value="csv" control={<Radio />} label="CSV File" />
        </RadioGroup>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
        <FormLabel component="legend">Include in Export</FormLabel>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeSummary}
                onChange={handleIncludeChange('includeSummary')}
              />
            }
            label="Overall Summary"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeFindings}
                onChange={handleIncludeChange('includeFindings')}
              />
            }
            label="Detailed Findings"
          />
          {config.format === 'pdf' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.includeSlideImages}
                  onChange={handleIncludeChange('includeSlideImages')}
                />
              }
              label="Slide Images"
            />
          )}
        </FormGroup>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend">Filter by Severity</FormLabel>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Only include findings with selected severities
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.severityFilter.includes('critical')}
                onChange={handleSeverityChange('critical')}
              />
            }
            label="Critical"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.severityFilter.includes('high')}
                onChange={handleSeverityChange('high')}
              />
            }
            label="High"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={config.severityFilter.includes('low')}
                onChange={handleSeverityChange('low')}
              />
            }
            label="Low"
          />
        </FormGroup>
      </FormControl>
    </Box>
  );
};

